import { Box, Group, Stack, Text } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";

import { DateFilter, PriorityFilter, TypeFilter } from "./useTodoFilters";

interface FilterItemProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  showCheckmark?: boolean;
}

const FilterItem = ({ label, isActive, onClick, showCheckmark = false }: FilterItemProps) => (
  <Box
    px="md"
    py="sm"
    style={{
      cursor: "pointer",
      borderRadius: "var(--mantine-radius-sm)",
      backgroundColor: isActive ? "var(--mantine-color-gray-1)" : "transparent",
    }}
    onClick={onClick}
  >
    {showCheckmark ? (
      <Group gap="xs">
        <IconCheck size={14} color="gray" />
        <Text fz={14} fw={isActive ? 600 : 400}>
          {label}
        </Text>
      </Group>
    ) : (
      <Text fz={14} fw={isActive ? 600 : 400}>
        {label}
      </Text>
    )}
  </Box>
);

interface FilterSidebarProps {
  dateFilter: DateFilter;
  setDateFilter: (filter: DateFilter) => void;
  typeFilter: TypeFilter;
  setTypeFilter: (filter: TypeFilter) => void;
  priorityFilter: PriorityFilter;
  setPriorityFilter: (filter: PriorityFilter) => void;
  archiveFilter: boolean;
  setArchiveFilter: (filter: boolean) => void;
}

const FilterSidebar = ({
  dateFilter,
  setDateFilter,
  typeFilter,
  setTypeFilter,
  priorityFilter,
  setPriorityFilter,
  archiveFilter,
  setArchiveFilter,
}: FilterSidebarProps) => {
  return (
    <Box
      flex={0.5}
      p="lg"
      style={{
        borderRight: "1px solid var(--mantine-color-gray-3)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        {/* Date filters */}
        <Stack gap={0}>
          <FilterItem
            label="Danas"
            isActive={dateFilter === "today"}
            onClick={() => setDateFilter("today")}
          />
          <FilterItem
            label="Sledećih 7 dana"
            isActive={dateFilter === "week"}
            onClick={() => setDateFilter("week")}
          />
          <FilterItem
            label="Odmor"
            isActive={dateFilter === "absence"}
            onClick={() => setDateFilter("absence")}
          />
          <FilterItem
            label="Svi"
            isActive={dateFilter === "all"}
            onClick={() => setDateFilter("all")}
          />
        </Stack>

        {/* Type filters */}
        <Text fz={13} c="dimmed" mt="xl" mb="xs">
          Tipovi To Do-a
        </Text>
        <Stack gap={0}>
          <FilterItem
            label="Svi tipovi"
            isActive={typeFilter === "all"}
            onClick={() => setTypeFilter("all")}
          />
          <FilterItem
            label="Pozivi"
            isActive={typeFilter === "CALL"}
            onClick={() => setTypeFilter("CALL")}
          />
          <FilterItem
            label="Sastanci"
            isActive={typeFilter === "MEETING"}
            onClick={() => setTypeFilter("MEETING")}
          />
          <FilterItem
            label="Interni zadaci"
            isActive={typeFilter === "INTERNAL_TASK"}
            onClick={() => setTypeFilter("INTERNAL_TASK")}
          />
        </Stack>

        {/* Priority filters */}
        <Text fz={13} c="dimmed" mt="xl" mb="xs">
          Važnost
        </Text>
        <Stack gap={0}>
          <FilterItem
            label="Hitno"
            isActive={priorityFilter === "urgent"}
            onClick={() => setPriorityFilter(priorityFilter === "urgent" ? "all" : "urgent")}
            showCheckmark
          />
          <FilterItem
            label="Važno"
            isActive={priorityFilter === "important"}
            onClick={() => setPriorityFilter(priorityFilter === "important" ? "all" : "important")}
            showCheckmark
          />
        </Stack>
      </Box>

      {/* Archive filter - positioned at bottom */}
      <Box>
        <FilterItem
          label="Završeni zadaci"
          isActive={archiveFilter}
          onClick={() => setArchiveFilter(!archiveFilter)}
        />
      </Box>
    </Box>
  );
};

export default FilterSidebar;
